import { useCallback, useMemo, useState } from 'react';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSearch, faStar } from '@fortawesome/free-solid-svg-icons';
import { Hide } from '@s20/components/common/Hide';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import ValidatedSelect, { CompactSelectStyles } from '../../common/forms/ValidatedSelect';
import {
    Flex, FlexColumnStart, IconButton, Input,
} from '../../common/forms/FormElements';
import { DatePicker } from '../../common/forms/DatePicker';
import { PlanningBoard } from '../../dispatch/PlanningBoard';
import { getPlanningBoard } from '../../dispatch/Dispatch';
import { useJobQueryKey } from './common/useJobQueryKey.hook';

export const Filters = ({ dispatchGroups }) => {
    const [preferredDispatchGroup, setPreferredDispatchGroup] = useState(() => window?.localStorage && window.localStorage.getItem('preferredDispatchGroup'));
    const { selectedDate, selectedPlanningFilter, dispatchGroupId } = useFormValues();

    const day = moment(selectedDate).toDate();

    const setFieldValue = useSetFieldValue();
    const query = useJobQueryKey();
    const { data: jobData } = useQuery(query);

    const planningBoard = useMemo(() => getPlanningBoard(jobData || [], [day]), [jobData, day]);
    const handleSelectPlanningFilter = useCallback((filter) => {
        if (filter === selectedPlanningFilter) {
            setFieldValue('selectedPlanningFilter', null);
        } else {
            setFieldValue('selectedPlanningFilter', filter);
        }
    }, [setFieldValue, selectedPlanningFilter]);

    const handleSetPreferredDispatchGroup = useCallback(() => {
        setPreferredDispatchGroup(dispatchGroupId);
        window.localStorage.setItem('preferredDispatchGroup', dispatchGroupId);
    }, [dispatchGroupId]);

    return (
        <header className="flex-none d-flex flex-column">
            <Flex
                justifyContent="end"
                alignItems="center"
                useColumnOnMobile={false}
                className="mb-2"
            >
                <Hide mdUp>
                    <Input
                        type="search"
                        placeholder="Search jobs"
                        formGroupClassName="mb-0 mr-2"
                        name="jobSearchText"
                        onChange={() => {
                            setFieldValue('selectedJobId', null);
                            setFieldValue('selectedResourceGroup', null);
                        }}
                        startAddonClassName="addon-centered"
                        startAddon={(
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="text-muted"
                            />
                        )}
                    />
                </Hide>

                <DatePicker
                    name="selectedDate"
                    formGroupClassName="mb-0 mr-2"
                    onChange={() => {
                        setFieldValue('selectedJobId', null);
                        setFieldValue('selectedResourceGroup', null);
                    }}
                />

                <ValidatedSelect
                    formGroupClassName="mb-0 mr-2"
                    name="dispatchGroupId"
                    placeholder="Select Dispatch Group"
                    options={dispatchGroups}
                    onChange={(value) => {
                        setFieldValue('selectedJobId', null);
                        setFieldValue('selectedResourceGroup', null);
                        setFieldValue('dispatchGroupCompanies', value?.companyIds ?? []);
                    }}
                    styles={{
                        ...CompactSelectStyles,
                        container: (base) => ({
                            ...base,
                            minWidth: 200,
                            flexShrink: 0,
                        }),
                    }}
                    formatOptionLabel={(opt, { context }) => {
                        if (context === 'menu') {
                            return (
                                <FlexColumnStart>
                                    <span>{opt.label}</span>

                                    {Boolean(opt.companies) && (
                                        <small className="mt-1">
                                            {opt.companies}
                                        </small>
                                    )}
                                </FlexColumnStart>
                            );
                        }

                        return <span>{opt.label}</span>;
                    }}
                    hideClear
                />

                {!!window.localStorage && (
                    <IconButton
                        title="Toggle this as preferred Dispatching"
                        onClick={handleSetPreferredDispatchGroup}
                        size="lg"
                    >
                        <FontAwesomeIcon
                            icon={faStar}
                            className={`preferred-dispatch-icon ${preferredDispatchGroup === dispatchGroupId ? 'active' : 'text-muted'}`}
                        />
                    </IconButton>
                )}

                {/* Mobile/Tablet Filters - Dropdown */}
                <Hide smDown>
                    <UncontrolledDropdown>
                        <IconButton
                            component={DropdownToggle}
                            type="button"
                        >
                            <FontAwesomeIcon
                                icon={faFilter}
                                className="text-muted"
                            />
                        </IconButton>

                        <DropdownMenu>
                            <DropdownItem header>Search Jobs</DropdownItem>
                            <div className="px-3 py-1">
                                <Input
                                    type="search"
                                    size="sm"
                                    name="jobSearchText"
                                    onChange={() => {
                                        setFieldValue('selectedJobId', null);
                                        setFieldValue('selectedResourceGroup', null);
                                    }}
                                />
                            </div>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </Hide>
            </Flex>

            <PlanningBoard
                planningBoard={planningBoard}
                selectedPlanningFilter={selectedPlanningFilter}
                handlePlanningStatusToggle={handleSelectPlanningFilter}
            />
        </header>
    );
};